import { memo, useCallback, useMemo, useState } from "react";

import { getBillingItemTypesAPI } from "services/api/finance/billing/api";
import { BillingItemType } from "services/api/finance/billing/types";
import { DropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

import { DropdownAutocomplete } from "ui/components/Dropdown/DropdownAutocomplete";
import { DropdownOption } from "ui/components/Dropdown/DropdownAutocomplete/types";

import { BillingItemNameFieldProps } from "./types";

const THIRTY_MINUTES = 30 * 60 * 1000;

/**
 * Naziv stavke i vrsta iz šifrarnika u jednom polju: odabir iz liste popuni cijeli redak
 * (šifra, cijena, PDV, JM, KPD), a upisani tekst ostaje kao slobodan naziv bez vrste.
 */
const BillingItemNameField: React.FC<BillingItemNameFieldProps> = (props) => {
  const {
    billingItemTypeId,
    itemName,
    onItemTypeSelect,
    onFreeTextChange,
    label = "Naziv stavke",
    placeholder = "Odaberite iz šifrarnika ili upišite naziv",
    error,
    disabled,
    required,
  } = props;

  const [search, setSearch] = useState<string>("");

  const { data } = useDropdownCache(
    DropdownCacheKey.BillingItemTypes,
    getBillingItemTypesAPI,
    THIRTY_MINUTES
  );

  const itemTypes: BillingItemType[] = useMemo(() => data ?? [], [data]);

  const options: DropdownOption[] = useMemo(() => {
    const term = search.trim().toLowerCase();

    const matching = itemTypes
      .filter((itemType) =>
        !term
        || itemType.name.toLowerCase().includes(term)
        || itemType.code.toLowerCase().includes(term)
      )
      .map((itemType) => ({
        value: itemType.id,
        text: itemType.name,
        group: itemType.billingItemKindName ?? undefined,
      }));

    // Slobodno upisan naziv nema svoju opciju, pa se dodaje da polje ne padne na placeholder.
    if (!billingItemTypeId && itemName) {
      return [{ value: itemName, text: itemName }, ...matching];
    }

    return matching;
  }, [itemTypes, search, billingItemTypeId, itemName]);

  const handleChange = useCallback(
    (_event: any, _name: string, item: DropdownOption) => {
      const selected = itemTypes.find((itemType) => itemType.id === item?.value);

      if (selected) {
        onItemTypeSelect(selected);
        return;
      }

      // Placeholder redak briše odabir, sinteticka opcija vraća isti slobodni naziv.
      onFreeTextChange(item?.value ? String(item.value) : "");
    },
    [itemTypes, onItemTypeSelect, onFreeTextChange]
  );

  const handleCreate = useCallback(
    (_event: any, value: string) => {
      onFreeTextChange(value);
    },
    [onFreeTextChange]
  );

  const handleSearchChange = useCallback((value: string) => setSearch(value), []);

  const handleSearchFocus = useCallback(() => setSearch(""), []);

  return (
    <DropdownAutocomplete
      name="billingItemName"
      label={label}
      placeholder={placeholder}
      value={billingItemTypeId ?? itemName}
      options={options}
      onChange={handleChange}
      onSearchChange={handleSearchChange}
      onSearchFocus={handleSearchFocus}
      onCreate={handleCreate}
      error={error}
      disabled={disabled}
      required={required}
    />
  );
};

export default memo(BillingItemNameField);
